import { useCallback, useEffect, useState } from 'react';
import useSettings from '../hooks/useSettings';

const THEMES = [
  {
    id: 'slate',
    name: 'Modern Slate (Standard)',
    description: 'Minimalistisches, modernes System-Design mit klaren Kontrasten und automatischem Dark-Mode.',
    colors: ['#0f172a', '#2563eb', '#f8fafc', '#ffffff'],
    fontFamily: 'System UI (-apple-system, Roboto, sans-serif)',
    tag: 'Empfohlen',
  },
  {
    id: 'editorial',
    name: 'Minimalist Editorial',
    description: 'Elegantes Serif-Design für Magazine & Blogs mit hoher Lesbarkeit und feiner Typografie.',
    colors: ['#1c1917', '#d97706', '#f5f5f4', '#ffffff'],
    fontFamily: 'Georgia, Cambria, "Times New Roman", serif',
    tag: 'Magazin / Blog',
  },
  {
    id: 'vibrant',
    name: 'Vibrant Neo (Creative)',
    description: 'Lebendiges Glassmorphism-Design mit leuchtenden Farbverläufen und abgerundeten Karten.',
    colors: ['#4f46e5', '#7c3aed', '#06b6d4', '#0f172a'],
    fontFamily: 'Outfit, Inter, sans-serif',
    tag: 'Kreativ / Modern',
  },
  {
    id: 'corporate',
    name: 'Corporate Clean',
    description: 'Klassisch strukturiertes Business-Design in Dunkelblau mit scharfen Kanten und klaren Linien.',
    colors: ['#1e3a8a', '#2563eb', '#f1f5f9', '#ffffff'],
    fontFamily: 'Helvetica, Arial, sans-serif',
    tag: 'Business / Enterprise',
  },
];

export default function ThemeSelector() {
  const settingsApi = useSettings();
  const [activeTheme, setActiveTheme] = useState('slate');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadTheme = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await settingsApi.getSettings();
      setActiveTheme(data.theme || 'slate');
    } catch (err) {
      setError(err.message || 'Fehler beim Laden des Website-Designs.');
    } finally {
      setLoading(false);
    }
  }, [settingsApi]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const selectTheme = async (themeId) => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const current = await settingsApi.getSettings();
      await settingsApi.updateSettings({
        ...current,
        theme: themeId,
      });
      setActiveTheme(themeId);
      await new Promise((r) => setTimeout(r, 1000));
      setMessage(`Design „${THEMES.find((t) => t.id === themeId)?.name}“ wurde erfolgreich aktiviert. Bitte klicke auf „Veröffentlichen“, um das neue Design auf S3/CloudFront anzuwenden.`);
    } catch (err) {
      setError(err.message || 'Fehler beim Aktivieren des Website-Designs.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack gap-lg">
      <section className="card">
        <div className="row between responsive-gap">
          <div>
            <h2>Website-Design & Themes</h2>
            <p className="muted">
              Wähle ein vorgefertigtes Design für die öffentlich publizierte S3/CloudFront Website aus.
            </p>
          </div>
        </div>
        {message ? <div className="alert success" style={{ marginTop: '1rem' }}>{message}</div> : null}
        {error ? <div className="alert error" style={{ marginTop: '1rem' }}>{error}</div> : null}
      </section>

      {loading ? (
        <p className="muted">Lade Designs…</p>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {THEMES.map((theme) => {
            const isActive = activeTheme === theme.id;
            return (
              <div
                key={theme.id}
                className="card"
                style={{
                  border: isActive ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  boxShadow: isActive ? '0 10px 30px rgba(37,99,235,0.15)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  position: 'relative',
                }}
              >
                {isActive && (
                  <span
                    className="badge"
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: '#2563eb',
                      color: '#fff',
                    }}
                  >
                    Aktiv ✓
                  </span>
                )}

                <div>
                  <span className="badge" style={{ background: '#f1f5f9', color: '#475569', marginBottom: '0.75rem' }}>
                    {theme.tag}
                  </span>
                  <h3 style={{ margin: '0.25rem 0 0.5rem', fontSize: '1.2rem' }}>{theme.name}</h3>
                  <p className="muted small" style={{ marginBottom: '1.25rem' }}>
                    {theme.description}
                  </p>

                  {/* Color Palette Swatches */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.35rem' }}>Farbpalette:</div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      {theme.colors.map((c, i) => (
                        <div
                          key={i}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: c,
                            border: '1px solid #cbd5e1',
                          }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Typography Sample */}
                  <div style={{ marginBottom: '1.5rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.2rem' }}>Typografie:</div>
                    <div style={{ fontFamily: theme.fontFamily, fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
                      Aa Bb Cc — Überschrift
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={`button ${isActive ? 'primary' : 'secondary'}`}
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => selectTheme(theme.id)}
                  disabled={saving || isActive}
                >
                  {isActive ? 'Aktiviertes Design' : 'Design aktivieren'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
